import { useRef } from 'react'
import dayjs from 'dayjs'
import { useHover } from 'usehooks-ts'

import CheckOutlinedIcon from '@ant-design/icons/CheckOutlined'
import DeleteOutlinedIcon from '@ant-design/icons/DeleteOutlined'
import EyeOutlinedIcon from '@ant-design/icons/EyeOutlined'
import FileOutlinedIcon from '@ant-design/icons/FileOutlined'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import Image from '@shared/components/src/Image'
import useDependencyKey from '@shared/hooks/src/useDependencyKey'
import useDialog from '@shared/hooks/src/useDialog'
import { isImage, isPdf } from '@shared/utils'
import { Document, Thumbnail } from '@shared/utils/src/pdf'

import Confirmation from '@components/Dialog/Confirmation'

import styles from './File.styles'

export default function File({ fullWidth = false, data, onDelete, selected = false, onClick }) {
  const ref = useRef(null)
  const isHovered = useHover(ref)

  const { name, filename, created } = data

  const confirmDelete = useDialog({
    component: Confirmation,
    props: ({ close }) => ({
      title: 'Delete File?',
      description: 'Delete the file? Once deleted there is no undo.',
      rejectLabel: 'Cancel',
      confirmLabel: 'Delete',
      onReject: () => close(),
      onConfirm: () => {
        const result = onDelete()
        if (result instanceof Promise) {
          return result.then(() => close())
        } else {
          close()
        }
      },
    }),
  })

  const handleView = (e) => {
    e.stopPropagation()
    window.open(data.url, '_blank')
  }

  return (
    <Card ref={ref} sx={[styles.card, selected && styles.selected, fullWidth && styles.fullWidth]} onClick={onClick || handleView}>
      <Box sx={styles.content}>
        <Preview file={data} />
        <Fade in={isHovered}>
          <Stack sx={styles.overlay}>
            <Stack direction="row" spacing={1} sx={{ p: 1, justifyContent: 'flex-end' }}>
              {onClick && (
                <IconButton variant="contained" color="primary" shape="rounded" size="small" onClick={handleView}>
                  <EyeOutlinedIcon style={styles.icon} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  variant="contained"
                  color="primary"
                  shape="rounded"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    confirmDelete()
                  }}
                >
                  <DeleteOutlinedIcon style={styles.icon} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Fade>
      </Box>
      {(name || filename || created) && (
        <Stack sx={styles.description}>
          {(name || filename) && (
            <Typography noWrap variant="subtitle2">
              {name || filename}
            </Typography>
          )}
          {created && (
            <Typography noWrap variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {dayjs(created).format('L LT')}
            </Typography>
          )}
        </Stack>
      )}
      {selected && (
        <Box sx={styles.checked}>
          <CheckIcon rotate={-45} sx={{ position: 'absolute', left: 14, bottom: 3 }} />
        </Box>
      )}
    </Card>
  )
}

export function Preview({ file, width = 150 }) {
  const key = useDependencyKey(file.url)

  if (isImage(file.mimetype)) {
    return <Image fit="cover" duration={500} src={file.url} errorIcon={<FileIcon />} />
  }

  if (isPdf(file.mimetype)) {
    return (
      <Document key={key} file={file.url}>
        <Thumbnail pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} width={width} />
      </Document>
    )
  }

  return <Unrecognized />
}

function Unrecognized() {
  return (
    <Box sx={styles.unrecognized}>
      <FileIcon />
    </Box>
  )
}

const CheckIcon = styled(CheckOutlinedIcon)(({ theme }) => ({
  color: theme.palette.background.paper,
  fontSize: 12,
}))

export const FileIcon = styled(FileOutlinedIcon)(({ theme }) => ({
  color: theme.palette.grey[400],
  fontSize: 40,
}))
